import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { PaymentPlanSelector } from '../components/PaymentPlanSelector';
import { CheckoutForm } from '../components/CheckoutForm';
import PromoCodeInput from '../components/PromoCodeInput';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Star,
  MapPin,
  Calendar,
  Users,
  Clock,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  BookOpen,
  Heart,
  UserPlus,
  Bed
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to compare itineraries for meaningful differences
// Returns true if itineraries are different, false if they're the same
const areItinerariesDifferent = (itinerary1, itinerary2) => {
  // If either is empty/null, they're different only if one has content
  if (!itinerary1?.length && !itinerary2?.length) return false;
  if (!itinerary1?.length || !itinerary2?.length) return true;
  if (itinerary1.length !== itinerary2.length) return true;
  
  // Compare each day's meaningful content
  for (let i = 0; i < itinerary1.length; i++) {
    const day1 = itinerary1[i];
    const day2 = itinerary2[i];
    
    // Compare day number, title, and description (the user-visible content)
    if (day1.day !== day2.day) return true;
    if ((day1.title || '') !== (day2.title || '')) return true;
    if ((day1.description || '') !== (day2.description || '')) return true;
  }
  
  return false; // All days match
};

// Mock solo traveler counts (in production from DB)
export default function TripDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [interestedCount, setInterestedCount] = useState(0);
  const [selectedDate, setSelectedDate] = useState('');
  const [useCustomDate, setUseCustomDate] = useState(false);
  const [customDate, setCustomDate] = useState('');
  const [dateBlocked, setDateBlocked] = useState(null);  // Blackout date check result
  const [travelers, setTravelers] = useState(1);
  const [paymentPlan, setPaymentPlan] = useState('full');
  const [roomType, setRoomType] = useState('shared'); // 'shared' or 'private' (legacy)
  const [selectedRoomType, setSelectedRoomType] = useState(null); // New room type system
  const [selectedAddOns, setSelectedAddOns] = useState([]); // Selected add-ons
  const [expandedDay, setExpandedDay] = useState(-1);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [relatedResources, setRelatedResources] = useState([]);
  
  // Split payment state
  const [splitPayment, setSplitPayment] = useState(false);
  const [coTravelerEmails, setCoTravelerEmails] = useState([]);
  
  // Location variant state (for trips with multiple locations)
  const [selectedVariant, setSelectedVariant] = useState(null);
  
  // Accommodation level state (for trips with different hotel tiers)
  const [selectedAccommodation, setSelectedAccommodation] = useState(null);
  
  // Promo code state
  const [appliedPromo, setAppliedPromo] = useState(null);
  
  // Checkout flow state
  const [checkoutStep, setCheckoutStep] = useState('selection'); // 'selection' | 'details' | 'waiver'
  const [checkoutData, setCheckoutData] = useState(null);
  
  // Solo traveler matching state
  const [showMatchingForm, setShowMatchingForm] = useState(false);
  const [matchingInterest, setMatchingInterest] = useState({
    preferredDates: '',
    lookingForRoommate: false,
    lookingForTravelPartner: false,
    bio: ''
  });

  useEffect(() => {
    fetchTrip();
    fetchRelatedResources();
  }, [slug]);

  const fetchTrip = async () => {
    try {
      const res = await axios.get(`${API}/trips/${slug}`);
      setTrip(res.data);
      
      // If trip has location variants, select the first one by default
      if (res.data.location_variants && res.data.location_variants.length > 0) {
        setSelectedVariant(res.data.location_variants[0]);
      }
      
      // If trip has accommodation levels, select the first one by default
      if (res.data.accommodation_levels && res.data.accommodation_levels.length > 0) {
        setSelectedAccommodation(res.data.accommodation_levels[0]);
      }
      
      if (res.data.tour_dates?.length > 0) {
        const firstDate = res.data.tour_dates[0];
        setSelectedDate(firstDate);
        // Check if the first date is within booking cutoff
        checkDateBlocked(firstDate, res.data.id, res.data.booking_cutoff_days || 14);
      }
      
      // Fetch interest count for this trip
      try {
        const interestRes = await axios.get(`${API}/trips/${slug}/interest-count`);
        setInterestedCount(interestRes.data.count || 0);
      } catch (e) {
        console.log('Could not fetch interest count');
      }
    } catch (err) {
      toast.error('Trip not found');
      navigate('/trips');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedResources = async () => {
    try {
      const res = await axios.get(`${API}/resources`);
      // Filter resources that have this trip in their related_trips
      const related = res.data.filter(r => r.related_trips.includes(slug));
      setRelatedResources(related.slice(0, 3));
    } catch (err) {
      console.error('Failed to fetch resources:', err);
    }
  };

  // Check if a date is blocked (blackout date) or within booking cutoff
  const checkDateBlocked = async (date, tripId, bookingCutoffDays = 14) => {
    if (!date) {
      setDateBlocked(null);
      return;
    }
    
    // Check booking cutoff (default 14 days before departure)
    const selectedDateObj = new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffTime = selectedDateObj - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < bookingCutoffDays) {
      setDateBlocked({
        blocked: true,
        reason: `Bookings must be made at least ${bookingCutoffDays} days before departure`
      });
      return;
    }
    
    try {
      const res = await axios.get(`${API}/admin/blackout-dates/check/${date}${tripId ? `?trip_id=${tripId}` : ''}`);
      setDateBlocked(res.data.blocked ? res.data : null);
    } catch (err) {
      setDateBlocked(null);
    }
  };

  // Handle custom date change with blackout check
  const handleCustomDateChange = (date) => {
    setCustomDate(date);
    setSelectedDate(date);
    if (trip?.id) {
      checkDateBlocked(date, trip.id, trip.booking_cutoff_days || 14);
    }
  };

  // Set default room type based on availability when trip loads
  useEffect(() => {
    if (trip?.room_options) {
      const opts = trip.room_options;
      if (opts.shared === false && opts.private !== false) {
        setRoomType('private');
      } else if (opts.private === false && opts.shared !== false) {
        setRoomType('shared');
      }
    }
  }, [trip]);

  // Get private room supplement from trip or use default
  const PRIVATE_ROOM_SUPPLEMENT = trip?.private_room_supplement || 350;
  
  // Get room availability options (legacy system)
  const roomOptions = trip?.room_options || { shared: true, private: true };
  
  // Get new room types (if defined)
  const roomTypes = trip?.room_types || [];
  const hasNewRoomTypes = roomTypes.length > 0;
  
  // Get active itinerary - check for traveler-based tiers within accommodation level
  const getActiveItinerary = () => {
    if (selectedAccommodation) {
      // Check if there's a traveler-based tier that matches the current traveler count
      const tiers = selectedAccommodation.traveler_itineraries || [];
      if (tiers.length > 0) {
        const matchingTier = tiers.find(tier => 
          travelers >= (tier.min_travelers || 1) && 
          travelers <= (tier.max_travelers || 999)
        );
        if (matchingTier?.itinerary?.length > 0) {
          return matchingTier.itinerary;
        }
      }
      // Fall back to accommodation level's default itinerary
      if (selectedAccommodation.itinerary?.length > 0) {
        return selectedAccommodation.itinerary;
      }
    }
    // Fall back to variant itinerary
    if (selectedVariant?.itinerary?.length > 0) {
      return selectedVariant.itinerary;
    }
    // Fall back to main trip itinerary
    return trip?.itinerary || [];
  };
  
  const activeItinerary = getActiveItinerary();
  const activeAddOns = selectedVariant?.add_ons || trip?.add_ons || [];
  const variantPriceAdjustment = selectedVariant?.price_adjustment || 0;
  const accommodationPriceAdjustment = selectedAccommodation?.price_adjustment || 0;
  
  // Calculate seasonal pricing adjustment based on selected date
  const getSeasonalPriceAdjustment = () => {
    if (!selectedDate || !trip?.seasonal_pricing?.length) return { amount: 0, rule: null };
    
    const dateToCheck = new Date(selectedDate);
    
    // Find matching seasonal rule
    for (const rule of trip.seasonal_pricing) {
      if (!rule.start_date || !rule.end_date) continue;
      
      const startDate = new Date(rule.start_date);
      const endDate = new Date(rule.end_date);
      
      // Check if selected date falls within the rule's date range
      if (dateToCheck >= startDate && dateToCheck <= endDate) {
        const adjustmentValue = rule.adjustment_value || 0;
        const adjustmentType = rule.adjustment_type || 'dollar';
        
        return {
          amount: adjustmentValue,
          type: adjustmentType,
          rule: rule
        };
      }
    }
    
    return { amount: 0, rule: null };
  };
  
  const seasonalAdjustment = getSeasonalPriceAdjustment();
  
  // Calculate base price - add solo adjustment if custom date selected
  const soloPriceAdjustment = (useCustomDate || selectedVariant?.has_group_dates === false) ? (trip?.solo_price_adjustment || 0) : 0;
  const basePriceBeforeSeasonal = trip ? trip.price + variantPriceAdjustment + accommodationPriceAdjustment + soloPriceAdjustment : 0;
  
  // Apply seasonal adjustment to base price
  const seasonalPriceAdjustment = seasonalAdjustment.rule 
    ? (seasonalAdjustment.type === 'percent' || seasonalAdjustment.type === 'percentage'
        ? (basePriceBeforeSeasonal * seasonalAdjustment.amount / 100)
        : seasonalAdjustment.amount)
    : 0;
  
  const basePrice = basePriceBeforeSeasonal + seasonalPriceAdjustment;
  
  // Calculate room price adjustment (can be fixed $ or percentage, positive or negative)
  const getRoomPriceAdjustment = () => {
    if (hasNewRoomTypes && selectedRoomType) {
      const adjustment = selectedRoomType.price_adjustment ?? selectedRoomType.price_supplement ?? 0;
      const adjustmentType = selectedRoomType.adjustment_type || 'fixed';
      
      if (adjustmentType === 'percentage') {
        // Calculate percentage of base price per person
        const percentageAmount = (basePrice * adjustment) / 100;
        return percentageAmount * travelers;
      }
      // Fixed amount per person
      return adjustment * travelers;
    }
    return roomType === 'private' ? PRIVATE_ROOM_SUPPLEMENT * travelers : 0;
  };
  
  // Get the display text for room price adjustment
  const getRoomAdjustmentDisplay = (rt) => {
    const adj = rt.price_adjustment ?? rt.price_supplement ?? 0;
    const type = rt.adjustment_type || 'fixed';
    
    if (adj === 0) return { text: 'Included', color: 'text-accent' };
    
    if (type === 'percentage') {
      const sign = adj > 0 ? '+' : '';
      return { 
        text: `${sign}${adj}%`, 
        color: adj > 0 ? 'text-secondary' : 'text-green-600' 
      };
    }
    
    // Fixed amount
    if (adj > 0) {
      return { text: `+$${adj}/person`, color: 'text-secondary' };
    }
    return { text: `-$${Math.abs(adj)}/person`, color: 'text-green-600' };
  };
  
  const roomPriceAdjustment = getRoomPriceAdjustment();
  const addOnsTotal = selectedAddOns.reduce((sum, addon) => sum + (addon.price * travelers), 0);
  const subtotalPrice = trip ? (basePrice * travelers) + roomPriceAdjustment + addOnsTotal : 0;
  const promoDiscount = appliedPromo?.discount_amount || 0;
  const totalPrice = subtotalPrice - promoDiscount;
  
  // Per-person price calculation for split payments
  const pricePerPerson = travelers > 0 ? Math.ceil(totalPrice / travelers) : totalPrice;

  // Toggle add-on selection
  const toggleAddOn = (addon) => {
    setSelectedAddOns(prev => {
      const exists = prev.find(a => a.id === addon.id);
      if (exists) {
        return prev.filter(a => a.id !== addon.id);
      } else {
        return [...prev, addon];
      }
    });
  };

  // Start checkout process - go to details form
  const handleStartCheckout = () => {
    if (!selectedDate) {
      toast.error('Please select a tour date');
      return;
    }
    setCheckoutStep('details');
    window.scrollTo(0, 0);
  };

  // Handle checkout form submission - go directly to payment
  const handleCheckoutFormSubmit = async (formData) => {
    const checkoutInfo = {
      ...formData,
      tripId: trip.id,
      tourDate: selectedDate,
      travelers,
      paymentPlan,
      roomType,
      totalAmount: totalPrice,
      specialRequests: formData.specialRequests || '',
      selectedAddOns: selectedAddOns,
      selectedVariant: selectedVariant,  // Include variant info
      selectedAccommodation: selectedAccommodation  // Include accommodation level info
    };
    setCheckoutData(checkoutInfo);
    
    // Go directly to Stripe payment (waiver will be collected after payment)
    await processPayment(checkoutInfo);
  };

  // Process payment directly without waiver
  const processPayment = async (data) => {
    if (!data || !data.email || !data.fullName) {
      toast.error('Missing checkout information. Please fill the form again.');
      return;
    }

    if (!trip || !trip.id || !selectedDate) {
      toast.error('Trip information is missing. Please refresh and try again.');
      return;
    }

    // For variant trips, require a variant selection
    if (trip.location_variants && trip.location_variants.length > 0 && !selectedVariant) {
      toast.error('Please select a location');
      return;
    }
    
    // For accommodation level trips, require a level selection
    if (trip.accommodation_levels && trip.accommodation_levels.length > 0 && !selectedAccommodation) {
      toast.error('Please select an accommodation level');
      return;
    }

    setBookingLoading(true);
    
    try {
      console.log('Starting checkout:', { trip_id: trip.id, email: data.email });

      const res = await axios.post(`${API}/guest/checkout`, {
        trip_id: trip.id,
        tour_date: selectedDate,
        travelers,
        payment_plan: paymentPlan,
        room_type: hasNewRoomTypes ? (selectedRoomType?.name || 'shared') : roomType,
        room_type_id: selectedRoomType?.id || null,
        room_type_name: selectedRoomType?.name || null,
        total_amount: subtotalPrice,  // Send original subtotal
        special_requests: data.specialRequests || '',
        guest_email: data.email,
        guest_name: data.fullName,
        guest_phone: data.phone || '',
        origin_url: window.location.origin,
        selected_add_ons: selectedAddOns || [],
        promo_code: appliedPromo?.code || null,
        discount_amount: promoDiscount,
        is_custom_date: useCustomDate,  // Flag for custom date bookings
        traveler_info: {
          dateOfBirth: data.dateOfBirth,
          roomType: hasNewRoomTypes ? (selectedRoomType?.name || 'shared') : roomType,
          roomTypeId: selectedRoomType?.id || null,
          specialRequests: data.specialRequests || '',
          passport: {
            number: data.passportNumber || '',
            expiry: data.passportExpiry || '',
            country: data.passportCountry || ''
          },
          emergencyContact: {
            name: data.emergencyName || '',
            phone: data.emergencyPhone || '',
            relationship: data.emergencyRelationship || ''
          },
          health: {
            dietaryRestrictions: data.dietaryRestrictions || '',
            allergies: data.allergies || '',
            medicalConditions: data.medicalConditions || ''
          },
          insurance: {
            hasInsurance: data.hasInsurance || false,
            provider: data.insuranceProvider || '',
            policyNumber: data.insurancePolicyNumber || ''
          },
          roommateRequest: data.roommateRequest || '',
          referralSource: data.referralSource || '',
          referralOther: data.referralOther || ''
        },
        // Location variant info if applicable
        selected_variant: selectedVariant ? {
          id: selectedVariant.id,
          name: selectedVariant.name,
          price_adjustment: selectedVariant.price_adjustment || 0
        } : null,
        // Accommodation level info if applicable
        selected_accommodation: selectedAccommodation ? {
          id: selectedAccommodation.id,
          name: selectedAccommodation.name,
          price_adjustment: selectedAccommodation.price_adjustment || 0
        } : null,
        // Account creation fields
        password: data.password || null,
        date_of_birth: data.dateOfBirth || null,
        referral_source: data.referralSource || null,
        // Split payment fields
        split_payment: splitPayment && travelers > 1,
        co_traveler_emails: splitPayment && travelers > 1 ? coTravelerEmails : [],
        price_per_person: pricePerPerson
      });

      console.log('Checkout response:', res.data);

      if (res.data && res.data.url) {
        toast.success('Redirecting to secure payment...');
        setTimeout(() => {
          window.location.href = res.data.url;
        }, 500);
      } else {
        console.error('No URL in response:', res.data);
        setBookingLoading(false);
        toast.error('Failed to get checkout URL. Please try again.');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setBookingLoading(false);
      // Handle Pydantic validation errors (array) vs string errors
      const detail = err.response?.data?.detail;
      let errorMessage = 'Failed to create booking. Please try again.';
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (err.message) {
        errorMessage = err.message;
      }
      toast.error(errorMessage);
    }
  };

  // Handle logged-in user booking (simplified flow)
  const handleLoggedInBooking = async () => {
    if (!selectedDate) {
      toast.error('Please select a tour date');
      return;
    }

    setBookingLoading(true);
    try {
      const res = await axios.post(
        `${API}/bookings`,
        {
          trip_id: trip.id,
          tour_date: selectedDate,
          travelers,
          payment_plan: paymentPlan,
          total_amount: totalPrice,
          special_requests: null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Booking created! Redirecting to payment...');
      
      // Create checkout session
      const checkoutRes = await axios.post(
        `${API}/payments/checkout`,
        {
          booking_id: res.data.id,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      window.location.href = checkoutRes.data.url;
    } catch (err) {
      // Handle Pydantic validation errors (array) vs string errors
      const detail = err.response?.data?.detail;
      let errorMessage = 'Failed to create booking';
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (err.message) {
        errorMessage = err.message;
      }
      toast.error(errorMessage);
    } finally {
      setBookingLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary" />
      </div>
    );
  }

  if (!trip) return null;

  // Checkout Details Form Step
  if (checkoutStep === 'details') {
    return (
      <div className="min-h-screen pt-24 pb-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="font-heading text-3xl font-bold text-primary mb-2">
              Complete Your Booking
            </h1>
            <p className="text-muted-foreground">
              {trip.title} • {travelers} traveler{travelers > 1 ? 's' : ''} • {selectedDate}
            </p>
            <div className="flex items-center gap-2 mt-2">
              {appliedPromo && (
                <span className="text-sm text-muted-foreground line-through">
                  ${subtotalPrice.toLocaleString()}
                </span>
              )}
              <p className="text-lg font-semibold text-secondary">
                Total: ${totalPrice.toLocaleString()}
              </p>
              {appliedPromo && (
                <Badge className="bg-green-100 text-green-700 ml-2">
                  {appliedPromo.code} Applied
                </Badge>
              )}
            </div>
          </div>
          
          <CheckoutForm
            tripType={trip.trip_type}
            onSubmit={handleCheckoutFormSubmit}
            onCancel={() => setCheckoutStep('selection')}
            loading={bookingLoading}
          />
        </div>
        
        {/* Loading overlay */}
        {bookingLoading && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100]">
            <div className="bg-white rounded-xl p-8 shadow-2xl flex flex-col items-center gap-4 max-w-sm mx-4">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-secondary border-t-transparent" />
              <div className="text-center">
                <p className="font-semibold text-primary text-lg">Connecting to payment...</p>
                <p className="text-muted-foreground text-sm mt-1">You'll be redirected to secure checkout</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Image */}
      <div className="relative h-[50vh] md:h-[60vh]">
        <img
          src={trip.image_url}
          alt={trip.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
          <div className="max-w-7xl mx-auto">
            <Badge className="bg-secondary text-white mb-4">
              {trip.trip_type === 'both' ? 'Group & Solo Available' : trip.trip_type === 'group' ? 'Group Tour' : 'Solo Adventure'}
            </Badge>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
              {trip.title}
            </h1>
            <div className="flex flex-wrap items-center gap-6 text-white/80">
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                <span>{trip.destination}, {trip.country}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                <span>{trip.duration_days} Days, {trip.duration_nights} Nights</span>
              </div>
              {trip.show_reviews && trip.rating > 0 && (
                <div className="flex items-center gap-2">
                  <Star className="h-5 w-5 fill-secondary text-secondary" />
                  <span>{trip.rating} ({trip.review_count} reviews)</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-12">
            {/* Description */}
            <section>
              <h2 className="font-heading text-2xl font-semibold text-primary mb-4">About This Trip</h2>
              <p className="text-muted-foreground leading-relaxed">{trip.description}</p>
            </section>

            {/* Highlights */}
            <section>
              <h2 className="font-heading text-2xl font-semibold text-primary mb-4">Trip Highlights</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {trip.highlights.map((highlight, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-secondary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="h-4 w-4 text-secondary" />
                    </div>
                    <span className="text-muted-foreground">{highlight}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Solo Traveler Matching Section */}
            <section>
              <Card className="bg-gradient-to-r from-cyan-50 to-teal-50 border-cyan-200">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center">
                        <Users className="h-6 w-6 text-cyan-600" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-primary">
                          Looking for a Travel Partner?
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {interestedCount > 0 
                            ? <><strong>{interestedCount}</strong> {interestedCount === 1 ? 'traveler is' : 'travelers are'} interested in this trip</>
                            : 'Be the first to express interest!'
                          }
                        </p>
                      </div>
                    </div>
                    <Button 
                      variant={showMatchingForm ? "outline" : "default"}
                      className={showMatchingForm ? "" : "bg-cyan-600 hover:bg-cyan-700"}
                      onClick={() => setShowMatchingForm(!showMatchingForm)}
                    >
                      {showMatchingForm ? (
                        <>
                          <X className="h-4 w-4 mr-2" />
                          Close
                        </>
                      ) : (
                        <>
                          <UserPlus className="h-4 w-4 mr-2" />
                          Express Interest
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {!showMatchingForm && (
                    <p className="text-sm text-muted-foreground">
                      Looking for a roommate to share costs or a travel buddy to explore with? 
                      Express your interest and we'll connect you with other solo travelers!
                    </p>
                  )}

                  {showMatchingForm && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="mt-4 pt-4 border-t border-cyan-200"
                    >
                      {user ? (
                        <div className="space-y-4">
                          <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                              <Label className="text-sm font-medium mb-1 block">When are you looking to travel?</Label>
                              <Input
                                placeholder="e.g., September 2025, Flexible"
                                value={matchingInterest.preferredDates}
                                onChange={(e) => setMatchingInterest({...matchingInterest, preferredDates: e.target.value})}
                              />
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-6">
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={matchingInterest.lookingForRoommate}
                                onCheckedChange={(v) => setMatchingInterest({...matchingInterest, lookingForRoommate: v})}
                              />
                              <Label className="text-sm">Looking for a roommate (share costs)</Label>
                            </div>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={matchingInterest.lookingForTravelPartner}
                                onCheckedChange={(v) => setMatchingInterest({...matchingInterest, lookingForTravelPartner: v})}
                              />
                              <Label className="text-sm">Looking for a travel partner</Label>
                            </div>
                          </div>
                          <div>
                            <Label className="text-sm font-medium mb-1 block">Quick intro (optional)</Label>
                            <Textarea
                              placeholder="Tell other travelers a bit about yourself..."
                              value={matchingInterest.bio}
                              onChange={(e) => setMatchingInterest({...matchingInterest, bio: e.target.value})}
                              rows={2}
                            />
                          </div>
                          <div className="flex gap-3">
                            <Button 
                              className="bg-cyan-600 hover:bg-cyan-700"
                              onClick={() => {
                                toast.success('Your interest has been saved! Other solo travelers can now find you.');
                                setShowMatchingForm(false);
                              }}
                            >
                              <Heart className="h-4 w-4 mr-2" />
                              Save My Interest
                            </Button>
                            <Link to="/member?tab=matching">
                              <Button variant="outline">
                                View All Matches
                              </Button>
                            </Link>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-muted-foreground mb-4">
                            Create a free account to connect with other solo travelers interested in this trip.
                          </p>
                          <div className="flex gap-3 justify-center">
                            <Link to="/register">
                              <Button className="bg-cyan-600 hover:bg-cyan-700">
                                Create Free Account
                              </Button>
                            </Link>
                            <Link to="/login">
                              <Button variant="outline">
                                Sign In
                              </Button>
                            </Link>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  )}
                </CardContent>
              </Card>
            </section>

            {/* Itinerary */}
            <section>
              <h2 className="font-heading text-2xl font-semibold text-primary mb-6">
                Day-by-Day Itinerary
                {selectedVariant && (
                  <span className="text-lg font-normal text-muted-foreground ml-2">
                    - {selectedVariant.name}
                  </span>
                )}
              </h2>
              <div className="space-y-4">
                {activeItinerary.map((day, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    <button
                      onClick={() => setExpandedDay(expandedDay === idx ? -1 : idx)}
                      className="w-full text-left"
                      data-testid={`itinerary-day-${day.day}`}
                    >
                      <div className={`flex items-center justify-between p-4 rounded-xl border transition-colors ${
                        expandedDay === idx ? 'bg-secondary/5 border-secondary/30' : 'bg-white border-border hover:border-secondary/30'
                      }`}>
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-secondary text-white flex items-center justify-center font-semibold">
                            {day.day}
                          </div>
                          <span className="font-medium text-primary">{day.title}</span>
                        </div>
                        {expandedDay === idx ? (
                          <ChevronUp className="h-5 w-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                    </button>
                    {expandedDay === idx && (
                      <div className="mt-2 p-4 bg-muted/30 rounded-xl ml-14">
                        {day.image && (
                          <div className="mb-4 rounded-lg overflow-hidden">
                            <img 
                              src={day.image} 
                              alt={day.title}
                              className="w-full h-48 object-cover"
                            />
                          </div>
                        )}
                        <p className="text-muted-foreground">{day.description}</p>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </section>

            {/* Included / Not Included */}
            <section className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-heading text-xl font-semibold text-primary mb-4">What's Included</h3>
                <ul className="space-y-2">
                  {trip.included.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="font-heading text-xl font-semibold text-primary mb-4">Not Included</h3>
                <ul className="space-y-2">
                  {trip.not_included.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <X className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          </div>

          {/* Booking Card */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <Card className="shadow-lg border-0" data-testid="booking-card">
                <CardHeader className="pb-4">
                  <div className="flex items-baseline justify-between">
                    <div>
                      <span className="text-sm text-muted-foreground">From</span>
                      <CardTitle className="font-heading text-3xl text-primary">
                        ${basePrice.toLocaleString()}
                      </CardTitle>
                      {seasonalAdjustment.rule && (
                        <div className="flex items-center gap-1 mt-1">
                          <Badge className={seasonalAdjustment.amount >= 0 ? 'bg-orange-100 text-orange-700 text-xs' : 'bg-green-100 text-green-700 text-xs'}>
                            {seasonalAdjustment.rule.name}: {seasonalAdjustment.amount >= 0 ? '+' : ''}
                            {seasonalAdjustment.type === 'percent' || seasonalAdjustment.type === 'percentage' 
                              ? `${seasonalAdjustment.amount}%` 
                              : `$${seasonalAdjustment.amount}`}
                          </Badge>
                        </div>
                      )}
                    </div>
                    <span className="text-sm text-muted-foreground">per person</span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Location Variant Selector */}
                  {trip.location_variants && trip.location_variants.length > 0 && (
                    <div>
                      <Label className="text-sm font-medium mb-2 block flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-secondary" />
                        Select Location
                      </Label>
                      <div className="grid grid-cols-1 gap-2">
                        {trip.location_variants.map((variant) => {
                          // Use has_group_dates from database (defaults to true for backwards compatibility)
                          const hasGroupDates = variant.has_group_dates !== false;
                          return (
                            <button
                              key={variant.id}
                              type="button"
                              onClick={() => {
                                setSelectedVariant(variant);
                                setSelectedAddOns([]); // Reset add-ons when location changes
                                // If variant doesn't have group dates, force custom date mode
                                if (!hasGroupDates) {
                                  setUseCustomDate(true);
                                  setSelectedDate('');
                                } else {
                                  // If variant has group dates, allow scheduled dates
                                  setUseCustomDate(false);
                                  setCustomDate('');
                                }
                              }}
                              className={`p-3 rounded-lg border-2 text-left transition-all ${
                                selectedVariant?.id === variant.id
                                  ? 'border-secondary bg-secondary/5'
                                  : 'border-gray-200 hover:border-secondary/50'
                              }`}
                              data-testid={`location-${variant.id}`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <p className="font-medium text-primary">{variant.name}</p>
                                    {variant.itinerary && variant.itinerary.length > 0 && 
                                     areItinerariesDifferent(variant.itinerary, trip.itinerary) && (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <span className="text-purple-600 cursor-help">
                                              <Calendar className="h-4 w-4" />
                                            </span>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            <p>Itinerary varies based on selection</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    )}
                                    {hasGroupDates ? (
                                      <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded font-medium">
                                        Group Dates
                                      </span>
                                    ) : (
                                      <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-medium">
                                        Select Date
                                      </span>
                                    )}
                                  </div>
                                  {variant.description && (
                                    <p className="text-xs text-muted-foreground mt-0.5">{variant.description}</p>
                                  )}
                                </div>
                                {variant.price_adjustment !== 0 && (
                                  <span className={`text-sm font-semibold ${variant.price_adjustment > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                                    {variant.price_adjustment > 0 ? `+$${variant.price_adjustment}` : `-$${Math.abs(variant.price_adjustment)}`}
                                  </span>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Number of Travelers - moved above accommodation */}
                  <div>
                    <Label className="text-sm font-medium mb-2 block flex items-center gap-2">
                      <Users className="h-4 w-4 text-secondary" />
                      Number of Travelers
                    </Label>
                    <Select 
                      value={travelers.toString()} 
                      onValueChange={(val) => {
                        const num = parseInt(val);
                        // Determine max from accommodation levels
                        let maxTravelers = 10;
                        if (trip?.accommodation_levels?.length > 0) {
                          const maxFromLevels = Math.max(
                            ...trip.accommodation_levels.map(level => level.max_travelers || 0)
                          );
                          maxTravelers = maxFromLevels > 0 ? maxFromLevels : (trip?.max_group_size || 10);
                        } else {
                          maxTravelers = trip?.max_group_size || 10;
                        }
                        
                        // If "Contact Us" option selected, navigate to contact page
                        if (num > maxTravelers) {
                          navigate('/contact');
                          return;
                        }
                        setTravelers(num);
                      }}
                    >
                      <SelectTrigger className="w-full" data-testid="travelers-select">
                        <SelectValue placeholder="Select travelers" />
                      </SelectTrigger>
                      <SelectContent>
                        {(() => {
                          // Determine max travelers from accommodation levels' traveler requirements
                          let maxTravelers = 10; // Default fallback
                          
                          if (trip?.accommodation_levels?.length > 0) {
                            // Find the highest max_travelers across all accommodation levels
                            const maxFromLevels = Math.max(
                              ...trip.accommodation_levels.map(level => level.max_travelers || 0)
                            );
                            if (maxFromLevels > 0) {
                              maxTravelers = maxFromLevels;
                            } else {
                              // No traveler requirements set, fall back to max_group_size
                              maxTravelers = trip?.max_group_size || 10;
                            }
                          } else {
                            // No accommodation levels, use trip's max_group_size
                            maxTravelers = trip?.max_group_size || 10;
                          }
                          
                          const options = [];
                          for (let i = 1; i <= maxTravelers; i++) {
                            options.push(
                              <SelectItem key={i} value={i.toString()}>
                                {i} {i === 1 ? 'Traveler' : 'Travelers'}
                              </SelectItem>
                            );
                          }
                          // Add "X+" option at one past max - clicking navigates to contact
                          options.push(
                            <SelectItem key="contact" value={(maxTravelers + 1).toString()} className="text-secondary font-medium">
                              {maxTravelers + 1}+ (Contact Us)
                            </SelectItem>
                          );
                          return options;
                        })()}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Accommodation Level Selector */}
                  {trip.accommodation_levels && trip.accommodation_levels.length > 0 && (
                    <div>
                      <Label className="text-sm font-medium mb-2 block flex items-center gap-2">
                        <Bed className="h-4 w-4 text-secondary" />
                        Accommodation Level
                      </Label>
                      <div className="grid grid-cols-1 gap-2">
                        {trip.accommodation_levels.map((level) => {
                          const minTravelers = level.min_travelers || 1;
                          const maxTravelers = level.max_travelers || 10;
                          const isAvailable = travelers >= minTravelers && travelers <= maxTravelers;
                          
                          // Auto-select first available if current selection becomes unavailable
                          if (selectedAccommodation?.id === level.id && !isAvailable) {
                            const firstAvailable = trip.accommodation_levels.find(l => {
                              const min = l.min_travelers || 1;
                              const max = l.max_travelers || 10;
                              return travelers >= min && travelers <= max;
                            });
                            if (firstAvailable && firstAvailable.id !== selectedAccommodation?.id) {
                              setTimeout(() => setSelectedAccommodation(firstAvailable), 0);
                            }
                          }
                          
                          return (
                            <button
                              key={level.id}
                              type="button"
                              onClick={() => isAvailable && setSelectedAccommodation(level)}
                              disabled={!isAvailable}
                              className={`p-3 rounded-lg border-2 text-left transition-all ${
                                !isAvailable
                                  ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                                  : selectedAccommodation?.id === level.id
                                    ? 'border-secondary bg-secondary/5'
                                    : 'border-gray-200 hover:border-secondary/50'
                              }`}
                              data-testid={`accommodation-${level.id}`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <p className="font-medium text-primary">{level.name}</p>
                                    {/* Show badge if default itinerary differs OR if there are traveler tiers */}
                                    {((level.itinerary && level.itinerary.length > 0 && 
                                     areItinerariesDifferent(level.itinerary, trip.itinerary)) ||
                                     (level.traveler_itineraries && level.traveler_itineraries.length > 0)) && (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <span className="text-purple-600 cursor-help">
                                              <Calendar className="h-4 w-4" />
                                            </span>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            <p>
                                              {level.traveler_itineraries?.length > 0 
                                                ? 'Itinerary varies based on group size'
                                                : 'Itinerary varies based on selection'}
                                            </p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    )}
                                    {!isAvailable && (
                                      <span className="text-[10px] px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded">
                                        {travelers < minTravelers 
                                          ? `${minTravelers}+ travelers` 
                                          : `Max ${maxTravelers} travelers`}
                                      </span>
                                    )}
                                  </div>
                                  {level.description && (
                                    <p className="text-xs text-muted-foreground mt-0.5">{level.description}</p>
                                  )}
                                </div>
                                {level.price_adjustment !== 0 && (
                                  <span className={`text-sm font-semibold ${!isAvailable ? 'text-gray-400' : level.price_adjustment > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                                    {level.price_adjustment > 0 ? `+$${level.price_adjustment}` : `-$${Math.abs(level.price_adjustment)}`}
                                  </span>
                                )}
                                {level.price_adjustment === 0 && (
                                  <span className="text-xs text-muted-foreground">Base Price</span>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Tour Date */}
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Select Tour Date</Label>
                    
                    {/* Check if selected variant requires custom date (no group dates) */}
                    {selectedVariant && selectedVariant.has_group_dates === false ? (
                      /* Variant without group dates - Must select custom date */
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-sm font-medium text-amber-800 mb-2">
                          Solo Trip Booking
                        </p>
                        <p className="text-sm text-amber-700 mb-3">
                          {selectedVariant.name} trips are available as solo bookings. Select your preferred travel date below.
                        </p>
                        <Input
                          type="date"
                          value={customDate}
                          onChange={(e) => handleCustomDateChange(e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          className="bg-white"
                          data-testid="custom-date-input"
                        />
                        {dateBlocked && (
                          <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            This date is not available. Please select another date.
                          </p>
                        )}
                        {customDate && !dateBlocked && (
                          <p className="text-xs text-green-600 mt-2">
                            ✓ We'll confirm availability for this date
                          </p>
                        )}
                      </div>
                    ) : trip.tour_dates && trip.tour_dates.length > 0 && !useCustomDate ? (
                      /* Variant with group dates or no variant - Show scheduled group dates */
                      <>
                        {selectedVariant?.has_group_dates !== false && (
                          <p className="text-xs text-green-700 mb-2 bg-green-50 px-2 py-1 rounded inline-block">
                            {trip.single_date_mode ? 'Scheduled departure' : 'Join a scheduled group departure'}
                          </p>
                        )}
                        
                        {/* Month/Year only display mode */}
                        {trip.date_display_mode === 'month_year' ? (
                          <div className="p-4 bg-secondary/5 border border-secondary/20 rounded-lg">
                            <p className="text-sm font-medium text-primary mb-1">
                              {(() => {
                                const [year, month] = trip.tour_dates[0].split('-');
                                const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                                return `${monthNames[parseInt(month) - 1]} ${year}`;
                              })()}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Exact dates to be announced
                            </p>
                            <button
                              type="button"
                              onClick={() => {
                                // Auto-select the month/year date
                                setSelectedDate(trip.tour_dates[0]);
                              }}
                              className={`mt-3 w-full py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                                selectedDate === trip.tour_dates[0]
                                  ? 'bg-secondary text-white'
                                  : 'bg-secondary/10 text-secondary hover:bg-secondary/20'
                              }`}
                            >
                              {selectedDate === trip.tour_dates[0] ? '✓ Selected' : 'Select This Date'}
                            </button>
                          </div>
                        ) : trip.single_date_mode && trip.tour_dates.length === 1 ? (
                          /* Single date mode - show as a card instead of dropdown */
                          <div className="p-4 bg-secondary/5 border border-secondary/20 rounded-lg">
                            <p className="text-sm font-medium text-primary mb-1">
                              {(() => {
                                const [year, month, day] = trip.tour_dates[0].split('-').map(Number);
                                const dateObj = new Date(year, month - 1, day);
                                return dateObj.toLocaleDateString('en-US', { 
                                  weekday: 'long',
                                  month: 'long', 
                                  day: 'numeric', 
                                  year: 'numeric' 
                                });
                              })()}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Only departure date for this trip
                            </p>
                            <button
                              type="button"
                              onClick={() => setSelectedDate(trip.tour_dates[0])}
                              className={`mt-3 w-full py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                                selectedDate === trip.tour_dates[0]
                                  ? 'bg-secondary text-white'
                                  : 'bg-secondary/10 text-secondary hover:bg-secondary/20'
                              }`}
                            >
                              {selectedDate === trip.tour_dates[0] ? '✓ Selected' : 'Select This Date'}
                            </button>
                          </div>
                        ) : (
                          /* Multiple dates - show dropdown */
                          <Select value={selectedDate} onValueChange={setSelectedDate}>
                            <SelectTrigger data-testid="tour-date-select">
                              <SelectValue placeholder="Choose a scheduled date" />
                            </SelectTrigger>
                            <SelectContent>
                              {trip.tour_dates.map((date) => {
                                // Parse date without timezone issues
                                const [year, month, day] = date.split('-').map(Number);
                                const dateObj = new Date(year, month - 1, day);
                                return (
                                  <SelectItem key={date} value={date}>
                                    {dateObj.toLocaleDateString('en-US', { 
                                      month: 'long', 
                                      day: 'numeric', 
                                      year: 'numeric' 
                                    })}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        )}

                        {/* Select Another Date Option - only show for non-single-date trips */}
                        {!trip.single_date_mode && (
                          <div className="mt-3">
                            <button
                              type="button"
                              onClick={() => {
                                setUseCustomDate(true);
                                setSelectedDate('');
                              }}
                              className="text-sm text-secondary hover:text-secondary/80 underline"
                              data-testid="toggle-custom-date"
                            >
                              Select another date instead
                            </button>
                          </div>
                        )}
                      </>
                    ) : trip.tour_dates && trip.tour_dates.length > 0 && useCustomDate && selectedVariant?.has_group_dates !== false ? (
                      /* User chose to select another date for a variant with group dates (wants custom instead) */
                      <div className="p-4 bg-secondary/5 border border-secondary/20 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-medium text-primary">Select Your Preferred Date</p>
                          <button
                            type="button"
                            onClick={() => {
                              setUseCustomDate(false);
                              setCustomDate('');
                              setSelectedDate('');
                              setDateBlocked(null);
                            }}
                            className="text-xs text-secondary hover:text-secondary/80 underline"
                          >
                            ← Back to scheduled dates
                          </button>
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">
                          Select your preferred date and we'll arrange a solo trip for you.
                        </p>
                        <Input
                          type="date"
                          value={customDate}
                          onChange={(e) => handleCustomDateChange(e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          className="bg-white"
                          data-testid="custom-date-input"
                        />
                        {dateBlocked && (
                          <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                            ⚠️ This date is unavailable: {dateBlocked.reason || 'Blocked'}
                          </p>
                        )}
                        {customDate && !dateBlocked && (
                          <p className="text-xs text-secondary mt-2">
                            ✓ Date available - this will be a solo booking
                          </p>
                        )}
                      </div>
                    ) : (
                      /* No scheduled dates - show date picker directly */
                      <div className="p-4 bg-secondary/5 border border-secondary/20 rounded-lg">
                        <p className="text-sm text-muted-foreground mb-3">
                          This trip is available on flexible dates. Choose your preferred travel date below.
                        </p>
                        <Input
                          type="date"
                          value={selectedDate}
                          onChange={(e) => handleCustomDateChange(e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          className="bg-white"
                          data-testid="flexible-date-input"
                        />
                        {dateBlocked && (
                          <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                            ⚠️ This date is unavailable: {dateBlocked.reason || 'Blocked'}
                          </p>
                        )}
                        {selectedDate && !dateBlocked && (
                          <p className="text-xs text-green-600 mt-2">
                            ✓ Date available
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Room Type - Only show if room options are configured */}
                  {(hasNewRoomTypes || roomOptions.shared !== false || roomOptions.private !== false) && (
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Room Preference</Label>
                    <div className="space-y-2">
                      {/* NEW Room Types System (if defined) */}
                      {hasNewRoomTypes ? (
                        <>
                          {roomTypes.map((rt) => {
                            const display = getRoomAdjustmentDisplay(rt);
                            return (
                              <button
                                key={rt.id}
                                type="button"
                                onClick={() => setSelectedRoomType(rt)}
                                disabled={rt.available <= 0}
                                className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                                  selectedRoomType?.id === rt.id 
                                    ? 'border-secondary bg-secondary/5' 
                                    : rt.available <= 0 
                                      ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                                      : 'border-border hover:border-secondary/50'
                                }`}
                                data-testid={`room-type-${rt.id}`}
                              >
                                <div className="flex gap-3">
                                  {/* Room Photo */}
                                  {rt.image_url && (
                                    <div className="w-16 h-16 rounded-md overflow-hidden flex-shrink-0">
                                      <img 
                                        src={rt.image_url} 
                                        alt={rt.name} 
                                        className="w-full h-full object-cover"
                                      />
                                    </div>
                                  )}
                                  <div className="flex-1 flex items-center justify-between">
                                    <div>
                                      <p className="font-medium text-primary">{rt.name}</p>
                                      {rt.description && (
                                        <p className="text-xs text-muted-foreground line-clamp-1">{rt.description}</p>
                                      )}
                                      <p className="text-xs text-muted-foreground">
                                        {rt.occupancy && `${rt.occupancy} person${rt.occupancy > 1 ? 's' : ''} per room • `}
                                        {rt.available > 0 ? `${rt.available} available` : 'Sold out'}
                                      </p>
                                    </div>
                                    <span className={`text-sm font-medium ${display.color} flex-shrink-0 ml-2`}>
                                      {display.text}
                                    </span>
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                          
                          {/* Private Room Option (always available alongside new room types) */}
                          {roomOptions.private !== false && (
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedRoomType(null);
                                setRoomType('private');
                              }}
                              className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                                roomType === 'private' && !selectedRoomType
                                  ? 'border-secondary bg-secondary/5' 
                                  : 'border-border hover:border-secondary/50'
                              }`}
                              data-testid="room-type-private"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium text-primary">Private Room</p>
                                  <p className="text-xs text-muted-foreground">Your own room, no sharing</p>
                                </div>
                                <span className="text-sm font-medium text-secondary">+${PRIVATE_ROOM_SUPPLEMENT}/person</span>
                              </div>
                            </button>
                          )}
                        </>
                      ) : (
                        <>
                          {/* LEGACY Room Options (Shared/Private only) */}
                          {/* Shared Room Option */}
                          {roomOptions.shared !== false && (
                            <button
                              type="button"
                              onClick={() => setRoomType('shared')}
                              className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                                roomType === 'shared' 
                                  ? 'border-secondary bg-secondary/5' 
                                  : 'border-border hover:border-secondary/50'
                              }`}
                              data-testid="room-type-shared"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium text-primary">Shared Room</p>
                                  <p className="text-xs text-muted-foreground">Share with another traveler</p>
                                </div>
                                <span className="text-sm font-medium text-accent">Included</span>
                              </div>
                            </button>
                          )}
                          
                          {/* Private Room Option */}
                          {roomOptions.private !== false && (
                            <button
                              type="button"
                              onClick={() => setRoomType('private')}
                              className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                                roomType === 'private' 
                                  ? 'border-secondary bg-secondary/5' 
                                  : 'border-border hover:border-secondary/50'
                              }`}
                              data-testid="room-type-private"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium text-primary">Private Room</p>
                                  <p className="text-xs text-muted-foreground">Your own room, no sharing</p>
                                </div>
                                <span className="text-sm font-medium text-secondary">+${PRIVATE_ROOM_SUPPLEMENT}/person</span>
                              </div>
                            </button>
                          )}
                        </>
                      )}
                    </div>
                    
                    {/* Roommate Matching Message for Shared Rooms */}
                    {(roomType === 'shared' || selectedRoomType) && roomOptions.shared !== false && (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-xs text-blue-800">
                          <span className="font-semibold">👥 Roommate Matching:</span> If you're booking without a specified roommate, you'll be matched with a same-gender traveler from the group. Want to room with a friend? Add them to your booking or let us know!
                        </p>
                      </div>
                    )}
                    
                    {/* Solo Price Notice */}
                    {(useCustomDate || selectedVariant?.has_group_dates === false) && trip?.solo_price_adjustment > 0 && (
                      <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-xs text-amber-800">
                          <span className="font-semibold">📅 Solo Date Pricing:</span> Custom date bookings include an additional ${trip.solo_price_adjustment} for personalized arrangements.
                        </p>
                      </div>
                    )}
                  </div>
                  )}

                  {/* Payment Plan */}
                  <PaymentPlanSelector
                    totalPrice={totalPrice}
                    selectedPlan={paymentPlan}
                    onPlanChange={setPaymentPlan}
                  />

                  {/* Split Payment Option - Only show for multiple travelers */}
                  {travelers > 1 && (
                    <div className="space-y-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-sm font-medium block text-blue-900">Split Payment</Label>
                          <p className="text-xs text-blue-700">
                            Each traveler pays ${pricePerPerson.toLocaleString()} instead of one person paying ${totalPrice.toLocaleString()}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setSplitPayment(!splitPayment);
                            if (!splitPayment) {
                              // Initialize co-traveler emails array (one less than total travelers)
                              setCoTravelerEmails(Array(travelers - 1).fill(''));
                            }
                          }}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            splitPayment ? 'bg-blue-600' : 'bg-gray-300'
                          }`}
                          data-testid="split-payment-toggle"
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              splitPayment ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                      
                      {splitPayment && (
                        <div className="space-y-3 pt-3 border-t border-blue-200">
                          <p className="text-xs text-blue-800 font-medium">
                            Enter email addresses for {travelers - 1} other traveler{travelers > 2 ? 's' : ''} to send payment links:
                          </p>
                          {coTravelerEmails.map((email, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <span className="text-xs text-blue-700 w-20">Traveler {idx + 2}:</span>
                              <Input
                                type="email"
                                value={email}
                                onChange={(e) => {
                                  const updated = [...coTravelerEmails];
                                  updated[idx] = e.target.value;
                                  setCoTravelerEmails(updated);
                                }}
                                placeholder="email@example.com"
                                className="flex-1 text-sm bg-white"
                                data-testid={`co-traveler-email-${idx}`}
                              />
                            </div>
                          ))}
                          <p className="text-xs text-blue-600">
                            💡 You'll pay your share (${pricePerPerson.toLocaleString()}) now. Other travelers will receive payment links via email.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Add-ons Section */}
                  {activeAddOns && activeAddOns.length > 0 && (
                    <div className="space-y-3">
                      <Label className="text-sm font-medium block">
                        Optional Add-ons
                        {selectedVariant && (
                          <span className="text-xs text-muted-foreground ml-2">
                            for {selectedVariant.name}
                          </span>
                        )}
                      </Label>
                      <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                        {activeAddOns.map((addon) => {
                          const isSelected = selectedAddOns.some(a => a.id === addon.id);
                          return (
                            <div
                              key={addon.id}
                              onClick={() => toggleAddOn(addon)}
                              className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                                isSelected 
                                  ? 'border-secondary bg-secondary/5' 
                                  : 'border-gray-200 hover:border-secondary/50'
                              }`}
                              data-testid={`addon-${addon.id}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                  isSelected ? 'border-secondary bg-secondary' : 'border-gray-300'
                                }`}>
                                  {isSelected && <Check className="h-3 w-3 text-white" />}
                                </div>
                                <div>
                                  <p className="font-medium text-sm text-primary">{addon.name}</p>
                                  <p className="text-xs text-muted-foreground">{addon.description}</p>
                                </div>
                              </div>
                              <span className="font-semibold text-secondary text-sm whitespace-nowrap">
                                +${addon.price}/person
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Solo trip group booking note */}
                  {(trip.trip_type === 'solo' || trip.trip_type === 'both') && (
                    <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3">
                      <p className="text-sm text-cyan-800">
                        <strong>Traveling with a group?</strong> This itinerary can be booked by groups on custom dates. Contact us to arrange your solo experience!
                      </p>
                    </div>
                  )}

                  {/* Promo Code */}
                  <PromoCodeInput
                    subtotal={subtotalPrice}
                    tripSlug={trip.slug}
                    appliedPromo={appliedPromo}
                    onApply={(promo) => setAppliedPromo(promo)}
                    onRemove={() => setAppliedPromo(null)}
                  />

                  {/* Total */}
                  <div className="pt-4 border-t">
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Base price ({travelers} {travelers === 1 ? 'traveler' : 'travelers'})</span>
                        <span className="text-primary">${(trip.price * travelers).toLocaleString()}</span>
                      </div>
                      {/* Room type pricing - new system with discount/supplement (fixed or percentage) */}
                      {hasNewRoomTypes && selectedRoomType && (() => {
                        const priceAdj = selectedRoomType.price_adjustment ?? selectedRoomType.price_supplement ?? 0;
                        const adjType = selectedRoomType.adjustment_type || 'fixed';
                        
                        if (priceAdj !== 0) {
                          // Calculate actual dollar amount
                          let actualAmount;
                          let labelSuffix = '';
                          
                          if (adjType === 'percentage') {
                            actualAmount = (basePrice * priceAdj / 100) * travelers;
                            labelSuffix = ` (${priceAdj}%)`;
                          } else {
                            actualAmount = priceAdj * travelers;
                          }
                          
                          return (
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">
                                {selectedRoomType.name}
                                {selectedRoomType.occupancy && ` (${selectedRoomType.occupancy} ppl/room)`}
                                {labelSuffix}
                              </span>
                              <span className={actualAmount > 0 ? 'text-secondary' : 'text-green-600'}>
                                {actualAmount > 0 ? '+' : '-'}${Math.abs(actualAmount).toLocaleString()}
                              </span>
                            </div>
                          );
                        }
                        return null;
                      })()}
                      {/* Room type pricing - legacy system */}
                      {!hasNewRoomTypes && roomType === 'private' && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Private room supplement</span>
                          <span className="text-secondary">+${(PRIVATE_ROOM_SUPPLEMENT * travelers).toLocaleString()}</span>
                        </div>
                      )}
                      {/* Private room for new system (when private is selected alongside room types) */}
                      {hasNewRoomTypes && !selectedRoomType && roomType === 'private' && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Private room supplement</span>
                          <span className="text-secondary">+${(PRIVATE_ROOM_SUPPLEMENT * travelers).toLocaleString()}</span>
                        </div>
                      )}
                      {selectedAddOns.length > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Add-ons ({selectedAddOns.length})</span>
                          <span className="text-secondary">+${addOnsTotal.toLocaleString()}</span>
                        </div>
                      )}
                      {appliedPromo && (
                        <div className="flex justify-between text-sm">
                          <span className="text-green-600 font-medium">Promo ({appliedPromo.code})</span>
                          <span className="text-green-600 font-medium">-${promoDiscount.toLocaleString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between pt-2 border-t">
                        <span className="font-medium text-primary">Total</span>
                        <div className="text-right">
                          {appliedPromo && (
                            <span className="text-sm text-muted-foreground line-through mr-2">
                              ${subtotalPrice.toLocaleString()}
                            </span>
                          )}
                          <span className="font-heading text-2xl font-bold text-primary">
                            ${totalPrice.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button
                      className="w-full bg-secondary hover:bg-secondary/90 text-white rounded-full py-6 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleStartCheckout}
                      disabled={bookingLoading || dateBlocked || !selectedDate}
                      data-testid="book-now-btn"
                    >
                      {bookingLoading ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                      ) : dateBlocked ? (
                        'Date Unavailable'
                      ) : !selectedDate ? (
                        'Select a Date'
                      ) : (
                        <>
                          Book Now
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-muted-foreground text-center mt-3">
                      Secure checkout powered by Stripe
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Related Resources */}
        {relatedResources.length > 0 && (
          <div className="mt-16 pt-12 border-t">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <BookOpen className="h-6 w-6 text-secondary" />
                <h2 className="font-heading text-2xl font-semibold text-primary">
                  Travel Resources
                </h2>
              </div>
              <Link to="/resources" className="text-secondary hover:text-secondary/80 text-sm font-medium flex items-center">
                View All
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedResources.map((resource) => (
                <Link key={resource.id} to={`/resources/${resource.slug}`}>
                  <Card className="group overflow-hidden border-0 shadow-sm hover:shadow-lg transition-all h-full">
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img
                        src={resource.image_url}
                        alt={resource.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    </div>
                    <CardContent className="p-4">
                      <div className="flex flex-wrap gap-1 mb-2">
                        {resource.categories.slice(0, 2).map((cat) => (
                          <Badge key={cat} variant="outline" className="text-xs">
                            {cat}
                          </Badge>
                        ))}
                      </div>
                      <h3 className="font-heading font-semibold text-primary group-hover:text-secondary transition-colors line-clamp-2">
                        {resource.title}
                      </h3>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
